import React, { type PropsWithoutRef, type Ref, cloneElement, createElement, forwardRef, memo, useContext, useMemo } from "react";
import * as Core from "@blueprintjs/core";
import classnames from "classnames";
import type { DataAttributes } from "~/types";
import { getAutomationProps } from "../hooks";
import { removeNonHtmlProps } from "../utils";
import { FormScopeContext } from "./formScope";
import { Icon, IconName, IconProps } from "./icon";
import type { Placement } from "./popover";
import { Spinner } from "./spinner";
import { type ToneProps, toneClass } from "./tone";
import { Tooltip } from "./tooltip";

interface ButtonExtensionProps extends ToneProps {
  square?: boolean;
  variant?: ButtonVariant;
  icon?: IconName | React.ReactElement<IconProps>;
  rightIcon?: IconName | React.ReactElement<IconProps>;
  iconSize?: number;
}

export interface ButtonProps
  extends
    ButtonExtensionProps,
    DataAttributes,
    PropsWithoutRef<Omit<Core.ButtonProps, "icon" | "intent" | "rightIcon" | "text" | "onClick" | "small" | "large" | "minimal" | "outlined" | "variant">>,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onFocus"> {
  label: string;
}

export const Button = memo(
  forwardRef((props: ButtonProps, ref: Ref<HTMLButtonElement>) => {
    const formScope = useContext(FormScopeContext);
    return <Core.Button {...toButtonNativeProps(props, formScope)} ref={ref} />;
  })
);

export interface AnchorButtonProps
  extends
    ButtonExtensionProps,
    DataAttributes,
    PropsWithoutRef<
      Omit<Core.AnchorButtonProps, "type" | "intent" | "icon" | "rightIcon" | "text" | "onClick" | "small" | "large" | "minimal" | "outlined" | "variant">
    >,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "onFocus"> {
  label: string;
}

export const AnchorButton = memo(
  forwardRef((props: AnchorButtonProps, ref: Ref<HTMLAnchorElement>) => {
    const formScope = useContext(FormScopeContext);
    return <Core.AnchorButton {...toAnchorButtonNativeProps(props, formScope)} ref={ref} />;
  })
);

export type ButtonVariant = Core.ButtonVariant | "elevated";

export interface IconButtonProps extends DataAttributes, ToneProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
  "aria-label": string;
  name?: string;

  className?: string;
  active?: boolean;
  disabled?: boolean;
  size?: Core.Size;
  loading?: boolean;
  variant?: ButtonVariant;
  square?: boolean;
  icon: IconName | React.ReactElement<IconProps>;
  iconSize?: number;
  tabIndex?: number;

  /** If true, aria-label will also appear in a tooltip on hover. */
  tooltip?: boolean | Placement;
}

export const IconButton = memo(
  forwardRef((props: IconButtonProps, ref: Ref<HTMLAnchorElement>) => {
    const {
      "aria-label": ariaLabel,
      "aria-expanded": ariaExpanded,
      active,
      className,
      disabled,
      icon,
      iconSize,
      id,
      size,
      loading,
      tone = "none",
      variant,
      square = false,
      tabIndex = 0,
      tooltip,
      onFocus,
      onBlur,
      onClick,
      onKeyDown,
      onKeyUp,
      ...htmlProps
    } = props;

    const formScope = useContext(FormScopeContext);

    // Disable tooltip if button is used for popover that is expanded
    const expanded = ariaExpanded === true || ariaExpanded === "true";

    const childTarget = loading ? (
      <Spinner key="loading" className={Core.Classes.BUTTON_SPINNER} size={Core.SpinnerSize.SMALL} />
    ) : (
      <Icon icon={icon} size={iconSize} />
    );

    const classNames = useMemo(
      () =>
        classnames(
          Core.Classes.BUTTON,
          toneClass(tone),
          {
            [Core.Classes.ACTIVE]: !disabled && active,
            [Core.Classes.DISABLED]: disabled,
            [Core.Classes.LARGE]: size === "large",
            [Core.Classes.SMALL]: size === "small",
            [Core.Classes.MINIMAL]: variant === "minimal",
            [Core.Classes.OUTLINED]: variant === "outlined",
            [Core.Classes.ROUND]: !square,
            elevated: variant === "elevated",
          },
          "icon-button",
          "no-right-element",
          className
        ),
      [tone, className, variant, square, size, disabled, active]
    );

    return (
      <Tooltip<React.AnchorHTMLAttributes<HTMLAnchorElement> & React.RefAttributes<HTMLAnchorElement>>
        content={ariaLabel}
        disabled={!tooltip || expanded}
        placement={typeof tooltip === "string" ? tooltip : "top"}
        renderTarget={renderTarget}
      />
    );

    function renderTarget(...args: Parameters<NonNullable<Core.TooltipProps["renderTarget"]>>) {
      const targetProps = args[0];

      const clonedTarget = cloneElement(childTarget, {
        tabIndex: childTarget.props.tabIndex ?? targetProps.tabIndex,
      });

      const elementProps = {
        ...targetProps,
        ref: Core.mergeRefs(targetProps.ref, ref),
        isOpen: undefined,
        className: classnames(classNames, targetProps.className),
        role: "button",
        disabled: "disabled" as "disabled" | undefined,
        tabIndex,
        onFocus: disabled ? undefined : mergeHandlers(onFocus, targetProps.onFocus),
        onBlur: disabled ? undefined : mergeHandlers(onBlur, targetProps.onBlur),
        onClick: disabled ? undefined : mergeHandlers(onClick, targetProps.onClick),
        onKeyDown: disabled ? undefined : mergeHandlers(onKeyDown, targetProps.onKeyDown),
        onKeyUp: disabled ? undefined : onKeyUp,
        ...getButtonAutomationProps(props, formScope),
        ...removeNonHtmlProps(htmlProps),
      };

      if (!disabled) {
        delete elementProps.disabled;
      }

      delete elementProps.isOpen;

      return createElement("a", elementProps, clonedTarget);
    }
  })
);

function mergeHandlers<T extends (...args: any) => any>(left: T | undefined, right: T | undefined): (...args: Parameters<T>) => void {
  return (...args: Parameters<T>): void => {
    left?.(...args);
    right?.(...args);
  };
}

function toButtonNativeProps(
  props: ButtonProps,
  formScope: FormScopeContext
): Core.ButtonProps & DataAttributes & React.ButtonHTMLAttributes<HTMLButtonElement> {
  const { className, children, variant, icon, iconSize, tone, rightIcon, square, label, onClick, ...restProps } = props;

  const classes = classnames(className, toneClass(tone), {
    "bp6-round": square !== true,
    elevated: variant === "elevated",
    "no-right-element": !rightIcon,
  });

  const nativeIcon: Core.MaybeElement | undefined = icon ? <Icon icon={icon} size={iconSize} /> : undefined;

  const nativeRightIcon: Core.MaybeElement | undefined = rightIcon ? <Icon icon={rightIcon} size={iconSize} /> : undefined;

  return {
    ...restProps,
    children,
    className: classes,
    onClick: (evt: React.MouseEvent) => onClick?.(evt as React.MouseEvent<HTMLButtonElement>),
    icon: nativeIcon,
    variant: variant === "elevated" ? undefined : variant,
    rightIcon: nativeRightIcon,
    text: children ? undefined : label,
    ...getButtonAutomationProps(props, formScope),
  };
}

function toAnchorButtonNativeProps(
  props: AnchorButtonProps,
  formScope: FormScopeContext
): Core.AnchorButtonProps & DataAttributes & React.AnchorHTMLAttributes<HTMLAnchorElement> {
  const { className, children, variant, icon, iconSize, tone, label, rightIcon, square, type, onClick, ...restProps } = props;

  const classes = classnames(className, toneClass(tone), {
    "bp6-round": square !== true,
    elevated: variant === "elevated",
    "no-right-element": !rightIcon,
  });

  const nativeIcon: Core.MaybeElement | undefined = icon ? <Icon icon={icon} size={iconSize} /> : undefined;

  const nativeRightIcon: Core.MaybeElement | undefined = rightIcon ? <Icon icon={rightIcon} size={iconSize} /> : undefined;

  return {
    ...restProps,
    className: classes,
    children,
    onClick: (evt: React.MouseEvent) => onClick?.(evt as React.MouseEvent<HTMLAnchorElement>),
    icon: nativeIcon,
    variant: variant === "elevated" ? undefined : variant,
    rightIcon: nativeRightIcon,
    text: children ? undefined : label,
    ...getButtonAutomationProps(props, formScope),
  };
}

function getButtonAutomationProps(props: ButtonProps | AnchorButtonProps | IconButtonProps, formScope: FormScopeContext) {
  const { label, id } = getAutomationProps(formScope, props, "button");

  return {
    "aria-label": label,
    id,
  };
}
